import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import {
  applyTemplatesToCity,
  deleteCityTask,
  deleteTemplate,
  listCityTaskData,
  toggleCityTask,
  upsertCityTask,
  upsertTemplate,
} from "@/lib/city-tasks.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Trash2, Plus, RefreshCw } from "lucide-react";

export const Route = createFileRoute("/_authenticated/city-tasks")({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getUser();
    if (!data.user) throw redirect({ to: "/login" as never });
  },
  component: CityTasksPage,
});

function CityTasksPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listCityTaskData);
  const { data, isLoading } = useQuery({
    queryKey: ["city-tasks-data"],
    queryFn: () => listFn(),
  });

  const [selectedCity, setSelectedCity] = useState<string>("");
  const cities = data?.cities ?? [];
  const templates = data?.templates ?? [];
  const tasks = data?.tasks ?? [];
  const canManage = !!data?.canManage;

  const cityId = selectedCity || cities[0]?.id || "";
  const cityTasks = useMemo(
    () => tasks.filter((t: any) => t.city_id === cityId).sort((a: any, b: any) => a.sort_order - b.sort_order),
    [tasks, cityId],
  );

  const invalidate = () => qc.invalidateQueries({ queryKey: ["city-tasks-data"] });

  const toggle = useServerFn(toggleCityTask);
  const toggleMut = useMutation({
    mutationFn: (v: { id: string; is_completed: boolean }) => toggle({ data: v }),
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e.message || "Failed"),
  });

  const apply = useServerFn(applyTemplatesToCity);
  const applyMut = useMutation({
    mutationFn: (v: { city_id: string }) => apply({ data: v }),
    onSuccess: (r: any) => {
      invalidate();
      toast.success(`Added ${r.added} tasks`);
    },
  });

  if (isLoading) return <div className="p-8">Loading…</div>;
  if (!data) return <div className="p-8">Access denied.</div>;

  return (
    <div className="mx-auto max-w-5xl p-6 space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">City Tasks</h1>
        <p className="text-muted-foreground">Track content coverage across Finnish cities.</p>
      </div>

      <Tabs defaultValue="checklist">
        <TabsList>
          <TabsTrigger value="checklist">Checklist</TabsTrigger>
          {canManage && <TabsTrigger value="city-tasks">Manage City Tasks</TabsTrigger>}
          {canManage && <TabsTrigger value="templates">Templates</TabsTrigger>}
        </TabsList>

        <TabsContent value="checklist" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Select City</CardTitle>
            </CardHeader>
            <CardContent className="flex gap-2 items-center">
              <Select value={cityId} onValueChange={setSelectedCity}>
                <SelectTrigger className="w-64"><SelectValue placeholder="Choose a city" /></SelectTrigger>
                <SelectContent>
                  {cities.map((c: any) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {canManage && cityId && (
                <Button variant="outline" size="sm" onClick={() => applyMut.mutate({ city_id: cityId })}>
                  <RefreshCw className="size-4 mr-1" /> Sync from templates
                </Button>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                Tasks · {cityTasks.filter((t: any) => t.is_completed).length}/{cityTasks.length} done
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {cityTasks.length === 0 && <p className="text-muted-foreground text-sm">No tasks yet.</p>}
              {cityTasks.map((t: any) => (
                <label
                  key={t.id}
                  className="flex items-start gap-3 rounded-2xl border p-3 hover:bg-accent/40 cursor-pointer"
                >
                  <Checkbox
                    checked={t.is_completed}
                    onCheckedChange={(v) => toggleMut.mutate({ id: t.id, is_completed: !!v })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className={t.is_completed ? "line-through text-muted-foreground" : "font-medium"}>
                      {t.title}
                    </div>
                    {t.description && <div className="text-sm text-muted-foreground">{t.description}</div>}
                    {t.is_completed && t.completed_at && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Completed {new Date(t.completed_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {canManage && (
          <TabsContent value="city-tasks">
            <ManageCityTasks cityId={cityId} setSelectedCity={setSelectedCity} cities={cities} tasks={cityTasks} onChange={invalidate} />
          </TabsContent>
        )}

        {canManage && (
          <TabsContent value="templates">
            <ManageTemplates templates={templates} onChange={invalidate} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function ManageCityTasks({
  cityId, setSelectedCity, cities, tasks, onChange,
}: {
  cityId: string;
  setSelectedCity: (v: string) => void;
  cities: any[];
  tasks: any[];
  onChange: () => void;
}) {
  const upsert = useServerFn(upsertCityTask);
  const del = useServerFn(deleteCityTask);
  const upsertMut = useMutation({ mutationFn: (v: any) => upsert({ data: v }), onSuccess: onChange });
  const delMut = useMutation({ mutationFn: (id: string) => del({ data: { id } }), onSuccess: onChange });
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>City Tasks</CardTitle>
        <Select value={cityId} onValueChange={setSelectedCity}>
          <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
          <SelectContent>
            {cities.map((c: any) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 items-end border-b pb-3">
          <div className="flex-1">
            <Input placeholder="Task title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="flex-1">
            <Input placeholder="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} />
          </div>
          <Button
            onClick={() => {
              if (!title.trim() || !cityId) return;
              upsertMut.mutate(
                { city_id: cityId, title: title.trim(), description: desc.trim() || null, sort_order: tasks.length * 10 + 10 },
                { onSuccess: () => { setTitle(""); setDesc(""); onChange(); } },
              );
            }}
          >
            <Plus className="size-4 mr-1" /> Add
          </Button>
        </div>
        {tasks.map((t: any) => (
          <div key={t.id} className="flex gap-2 items-center">
            <Input
              defaultValue={t.title}
              onBlur={(e) => {
                if (e.target.value !== t.title) {
                  upsertMut.mutate({ id: t.id, city_id: cityId, title: e.target.value, description: t.description, sort_order: t.sort_order });
                }
              }}
            />
            <Button variant="ghost" size="icon" onClick={() => delMut.mutate(t.id)}>
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function ManageTemplates({ templates, onChange }: { templates: any[]; onChange: () => void }) {
  const upsert = useServerFn(upsertTemplate);
  const del = useServerFn(deleteTemplate);
  const upsertMut = useMutation({ mutationFn: (v: any) => upsert({ data: v }), onSuccess: onChange });
  const delMut = useMutation({ mutationFn: (id: string) => del({ data: { id } }), onSuccess: onChange });
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");

  return (
    <Card>
      <CardHeader>
        <CardTitle>Task Templates</CardTitle>
        <p className="text-sm text-muted-foreground">Applied automatically to new cities. Use "Sync from templates" on a city to add missing ones.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 items-end border-b pb-3">
          <Input placeholder="Template title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Input placeholder="Description" value={desc} onChange={(e) => setDesc(e.target.value)} />
          <Button
            onClick={() => {
              if (!title.trim()) return;
              upsertMut.mutate(
                { title: title.trim(), description: desc.trim() || null, sort_order: templates.length * 10 + 10, active: true },
                { onSuccess: () => { setTitle(""); setDesc(""); onChange(); } },
              );
            }}
          >
            <Plus className="size-4 mr-1" /> Add
          </Button>
        </div>
        {templates.map((t: any) => (
          <div key={t.id} className="flex gap-2 items-center">
            <Input
              defaultValue={t.title}
              onBlur={(e) => {
                if (e.target.value !== t.title) {
                  upsertMut.mutate({ id: t.id, title: e.target.value, description: t.description, sort_order: t.sort_order, active: t.active });
                }
              }}
            />
            <Checkbox
              checked={t.active}
              onCheckedChange={(v) => upsertMut.mutate({ id: t.id, title: t.title, description: t.description, sort_order: t.sort_order, active: !!v })}
            />
            <span className="text-xs text-muted-foreground w-16">{t.active ? "Active" : "Inactive"}</span>
            <Button variant="ghost" size="icon" onClick={() => delMut.mutate(t.id)}>
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
